/**
 * The TSR Dashboard: the house opening page, as a prefilled Custom Section.
 *
 * It is a preset rather than a section type of its own, and that is the whole
 * design. A dedicated type would have meant six band renderers across DOCX,
 * PDF and the preview, plus rebuilding the reorder and remove UI that the
 * composite already has. As a composite every band is editable, removable and
 * reorderable, and any other component can be added beside them, for free.
 *
 * The trade-off, stated plainly: this is a SNAPSHOT. Improving the preset
 * later does not reach dashboards already inserted into templates. That is the
 * price of the modularity, and it is the right way round -- a tester who has
 * tuned their dashboard should not have it rewritten underneath them.
 *
 * Every figure is a report variable, so the page is live against the
 * engagement rather than a set of numbers somebody has to remember to update.
 */

const NAVY = '#1f3864';
const CAPTION = '#2e86c1';
const WHITE = '#ffffff';
const INK = '#1f3864';
const PANEL = '#f2f5f9';
const MUTED = '#5f6b7a';

/** Small blue band captions, matching the house dashboard. */
const caption = (text) => ({
  type: 'heading',
  config: { text, level: 3, align: 'left', color: CAPTION },
});

export function tsrDashboardConfig() {
  return {
    heading: 'Dashboard',
    components: [
      // Who, what, and when -- the strip across the top.
      {
        type: 'metric-tiles',
        config: {
          heading: '', perRow: 4, labelSize: 7, valueSize: 12, upperCaseLabels: true,
          tiles: [
            { label: 'Client', value: '%CLIENTNAME%', bg: NAVY, labelColor: '#c8d4e8', valueColor: WHITE, align: 'left' },
            { label: 'Application', value: '%APPLICATIONNAME%', bg: NAVY, labelColor: '#c8d4e8', valueColor: WHITE, align: 'left' },
            { label: 'Assessment', value: '%ENGAGEMENTTYPE%', bg: NAVY, labelColor: '#c8d4e8', valueColor: WHITE, align: 'left' },
            { label: 'Testing window', value: '%STARTDATE% – %ENDDATE%', bg: NAVY, labelColor: '#c8d4e8', valueColor: WHITE, align: 'left' },
          ],
        },
      },

      caption('Findings by severity'),
      {
        type: 'metric-tiles',
        config: {
          heading: '', perRow: 5, labelSize: 8, valueSize: 20, upperCaseLabels: true,
          tiles: [
            { label: 'Critical', value: '%CRITICALCOUNT%', bg: '#7f1d1d', labelColor: WHITE, valueColor: WHITE },
            { label: 'High', value: '%HIGHCOUNT%', bg: '#dc2626', labelColor: WHITE, valueColor: WHITE },
            { label: 'Medium', value: '%MEDIUMCOUNT%', bg: '#ea580c', labelColor: WHITE, valueColor: WHITE },
            { label: 'Low', value: '%LOWCOUNT%', bg: '#ca8a04', labelColor: WHITE, valueColor: WHITE },
            { label: 'Informational', value: '%INFOCOUNT%', bg: CAPTION, labelColor: WHITE, valueColor: WHITE },
          ],
        },
      },

      caption('Key metrics'),
      {
        type: 'metric-tiles',
        config: {
          heading: '', perRow: 6, labelSize: 7, valueSize: 16, upperCaseLabels: false,
          // OWASP reads "categories with findings", never "n of 10": a fraction
          // would claim test coverage, and a findings table cannot tell
          // tested-and-clean from never-tested. The unclassified count sits
          // beside it so the number is visibly a lower bound.
          tiles: [
            { label: 'Total findings', value: '%TOTALFINDINGS%', bg: PANEL, labelColor: MUTED, valueColor: INK },
            { label: 'Exploitation demonstrated', value: '%EXPLOITABLECOUNT%', bg: PANEL, labelColor: MUTED, valueColor: INK },
            { label: 'Open findings', value: '%OPENCOUNT%', bg: PANEL, labelColor: MUTED, valueColor: INK },
            { label: 'Verified closed', value: '%CLOSEDCOUNT%', bg: PANEL, labelColor: MUTED, valueColor: INK },
            { label: 'OWASP categories with findings', value: '%OWASPCATEGORIES%', bg: PANEL, labelColor: MUTED, valueColor: INK },
            // The counterweight the comment above promises, and it was missing.
            // Until findings are classified this reads 0 beside a real finding
            // count, which states on a client deliverable that nothing maps to
            // the Top 10. With the unclassified figure next to it the zero
            // reads as "not yet classified", which is what it means.
            { label: 'Findings unclassified', value: '%OWASPUNCLASSIFIED%', bg: PANEL, labelColor: MUTED, valueColor: INK },
          ],
        },
      },

      caption('Scope snapshot'),
      {
        // Two columns, label and value. A row is left blank unless there is a
        // variable that actually answers it: a plausible-looking wrong value on
        // a client deliverable is worse than an obvious blank. In-scope asset
        // still has no variable and stays a prompt; approach now does.
        type: 'custom-table',
        config: {
          heading: '',
          tableStyle: { showHeader: false, borderColor: '#d8dfe8', borderStyle: 'full' },
          columns: [{ label: '', width: 28 }, { label: '', width: 72 }],
          rows: [
            ['In-scope asset', ''],
            ['Approach / access', '%TESTINGAPPROACH%'],
            ['Methodology', 'PTES · OWASP WSTG · Top 10 (2021) · NIST SP 800-115'],
            ['Lead tester / QA', '%TESTERNAME%'],
          ],
          cellStyles: [
            [{ bold: true, bg: PANEL, textColor: INK }, {}],
            [{ bold: true, bg: PANEL, textColor: INK }, {}],
            [{ bold: true, bg: PANEL, textColor: INK }, {}],
            [{ bold: true, bg: PANEL, textColor: INK }, {}],
          ],
        },
      },

      // No heading of its own: the caption above already says it, and the
       // chart's own heading renders as a full Heading 1, so the page carried
       // the same words twice at two sizes a few inches apart.
      { type: 'severity-chart', config: { heading: '', chartType: 'bar' } },
    ],
  };
}

export default tsrDashboardConfig;
